import { useEffect, useState } from "react";

import { IStaffDetails, IStaffUser } from "../../../../@types/admin/AdminStaff";
import { useUpdateStaff } from "../../../../hooks/mutations/admin/staff";
import {
  useHospitalOptions,
  useRoleOptions,
  useWardOptions,
} from "../../../../hooks/queries/admin/selectValues";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  CheckboxField,
  FieldError,
  FilterButton,
  FieldHint,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FormGrid,
  ReadOnlyValue,
  SectionActions,
  SectionCaption,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
} from "../AdminPage.styles";
import { AdminSelectField } from "../AdminSelectField";
import { ReactivateStaffModal } from "./ReactivateStaffModal";
import { ActionNote, WardChip, WardChipList } from "./StaffDetailsPanel.styles";

/**
 * Access scope. The labels are Retool's and deliberately don't match the
 * codes: `hospital-group` means the whole hospital, `hospital` means a single
 * ward (a ward IS a `cms_hospital` row).
 */
const PERMISSION_LEVELS = [
  { value: "hospital-group", label: "Hospital" },
  { value: "hospital", label: "Ward" },
  { value: "hospital-group-hierarchy", label: "Hospital Hierarchy (Legacy)" },
];

/** The API requires a ward only for single-ward (`hospital`) access. */
const WARD_SCOPED_PERMISSION = "hospital";

interface IStaffRecordFormProps {
  staff: IStaffDetails;
  /** Drives the re-activate button; undefined while the account is loading. */
  staffUser?: IStaffUser;
}

/** Per-field validation messages, keyed by the field they sit under. */
interface IStaffFormErrors {
  hospitalId?: string;
  permissionLevel?: string;
  wardId?: string;
}

interface IStaffFormState {
  hospitalId: string;
  permissionLevel: string;
  wardId: string;
  role: string;
  receiveAlerts: boolean;
  healthProviderId: string;
}

function toFormState(staff: IStaffDetails): IStaffFormState {
  return {
    hospitalId: staff.hospitalId ? String(staff.hospitalId) : "",
    permissionLevel: staff.permissionLevel ?? "",
    wardId: staff.wardId ? String(staff.wardId) : "",
    role: staff.role ?? "",
    receiveAlerts: staff.receiveAlerts,
    healthProviderId: staff.healthProviderId ?? "",
  };
}

/**
 * The staff record half of the details panel — Retool's `formStaffDetails`
 * (EN-5150). Ward selection cascades from the hospital, and the ward field
 * only applies to ward-scoped access; for hospital-wide access Retool showed
 * a disabled list of the hospital's wards instead, which is reproduced here
 * as read-only chips.
 */
export function StaffRecordForm({ staff, staffUser }: IStaffRecordFormProps) {
  const { showToast } = useToast();
  const { hospitals, isLoading: hospitalsLoading } = useHospitalOptions();
  const { roles, isLoading: rolesLoading } = useRoleOptions();
  const { updateStaff, isUpdatingStaff } = useUpdateStaff();
  const { openModal, closeModal } = useModal();

  const [form, setForm] = useState<IStaffFormState>(() => toFormState(staff));

  // Re-seed whenever a different staff member (or a fresh copy) arrives.
  useEffect(() => {
    setForm(toFormState(staff));
  }, [staff]);

  const hospitalId = form.hospitalId ? Number(form.hospitalId) : undefined;
  const { wards, isLoading: wardsLoading } = useWardOptions(hospitalId);

  const isWardScoped = form.permissionLevel === WARD_SCOPED_PERMISSION;
  const isAccountActive = staffUser?.isActive ?? true;

  const errors: IStaffFormErrors = {
    hospitalId: form.hospitalId ? undefined : "Select a hospital.",
    permissionLevel: form.permissionLevel
      ? undefined
      : "Select a permission level.",
    wardId:
      isWardScoped && !form.wardId ? "Ward access needs a ward." : undefined,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const handleHospitalChange = (value: string) => {
    // Clearing the ward mirrors Retool's change-event chain: a ward from the
    // previous hospital would no longer be a valid option.
    setForm((prev) => ({ ...prev, hospitalId: value, wardId: "" }));
  };

  const handleSave = async () => {
    if (hasErrors) return;

    try {
      await updateStaff({
        id: staff.id,
        permissionLevel: form.permissionLevel,
        hospitalId,
        // Only sent for ward-scoped access — Retool keyed this off the ward
        // select's visibility, and the API rejects a null ward for `hospital`.
        wardId: isWardScoped ? Number(form.wardId) : undefined,
        // Not editable in this form; carried through so the PUT doesn't clear it.
        isAdmin: staff.isAdmin,
        receiveMeasurementAlerts: form.receiveAlerts,
        role: form.role || undefined,
        healthProviderId: form.healthProviderId.trim(),
      });
      showToast("The staff details were edited successfully.", "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error editing the staff details."
        ),
        "warning"
      );
    }
  };

  const handleReactivateClick = () => {
    openModal(
      <ReactivateStaffModal
        staff={staff}
        staffName={
          staffUser
            ? `${staffUser.firstName} ${staffUser.lastName}`.trim()
            : undefined
        }
        onClose={closeModal}
      />,
      { width: "440px", showCloseButton: true }
    );
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionHeading>
          <SectionTitle>Staff details</SectionTitle>
          <SectionCaption>ID {staff.id}</SectionCaption>
        </SectionHeading>
      </SectionHeader>

      <FormGrid>
        <FilterGroup>
          <FilterLabel htmlFor="staff-hospital">Hospital</FilterLabel>
          <AdminSelectField
            id="staff-hospital"
            value={form.hospitalId}
            disabled={hospitalsLoading}
            onChange={(e) => handleHospitalChange(e.target.value)}
          >
            <option value="">
              {hospitalsLoading ? "Loading..." : "Select an option"}
            </option>
            {hospitals.map((hospital) => (
              <option key={hospital.id} value={hospital.id}>
                {hospital.name}
              </option>
            ))}
          </AdminSelectField>
          {errors.hospitalId && <FieldError>{errors.hospitalId}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="staff-permissions">Permissions</FilterLabel>
          <AdminSelectField
            id="staff-permissions"
            value={form.permissionLevel}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, permissionLevel: e.target.value }))
            }
          >
            <option value="">Select an option</option>
            {PERMISSION_LEVELS.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </AdminSelectField>
          {errors.permissionLevel && (
            <FieldError>{errors.permissionLevel}</FieldError>
          )}
        </FilterGroup>

        {isWardScoped && (
          <FilterGroup>
            <FilterLabel htmlFor="staff-ward">Ward</FilterLabel>
            <AdminSelectField
              id="staff-ward"
              value={form.wardId}
              disabled={!hospitalId || wardsLoading}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, wardId: e.target.value }))
              }
            >
              <option value="">
                {wardsLoading ? "Loading..." : "Select an option"}
              </option>
              {wards.map((ward) => (
                <option key={ward.id} value={ward.id}>
                  {ward.name}
                </option>
              ))}
            </AdminSelectField>
            {errors.wardId && <FieldError>{errors.wardId}</FieldError>}
          </FilterGroup>
        )}

        <FilterGroup>
          <FilterLabel htmlFor="staff-role">Role</FilterLabel>
          <AdminSelectField
            id="staff-role"
            value={form.role}
            disabled={rolesLoading}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, role: e.target.value }))
            }
          >
            <option value="">
              {rolesLoading ? "Loading..." : "Select an option"}
            </option>
            {roles.map((role) => (
              <option key={role.code} value={role.code}>
                {role.name}
              </option>
            ))}
            {/* A role the catalogue no longer offers (e.g. super-admin, or a
                retired code) would otherwise blank the field on load. */}
            {form.role && !roles.some((role) => role.code === form.role) && (
              <option value={form.role}>{form.role}</option>
            )}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="staff-health-provider-id">
            Health provider ID
          </FilterLabel>
          <FilterInput
            id="staff-health-provider-id"
            value={form.healthProviderId}
            maxLength={64}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, healthProviderId: e.target.value }))
            }
          />
          <FieldHint>
            Optional. Transmural Platform orders for this HCP are matched on it.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Alerts</FilterLabel>
          <CheckboxField>
            <input
              type="checkbox"
              checked={form.receiveAlerts}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  receiveAlerts: e.target.checked,
                }))
              }
            />
            Receives measurement alerts
          </CheckboxField>
        </FilterGroup>

        {/* Hospital-wide access covers every ward, so they are listed rather
            than chosen. Spans the grid because the chips wrap to several
            lines and would otherwise stretch the row beside them. */}
        {!isWardScoped && (
          <FilterGroup span="full">
            <FilterLabel>Wards</FilterLabel>
            {staff.wards.length > 0 ? (
              <WardChipList>
                {staff.wards.map((ward) => (
                  <WardChip key={ward}>{ward}</WardChip>
                ))}
              </WardChipList>
            ) : (
              <ReadOnlyValue>No wards</ReadOnlyValue>
            )}
          </FilterGroup>
        )}
      </FormGrid>

      <SectionActions>
        <ActionNote>
          {isAccountActive
            ? "This account is active."
            : "This account is deactivated — sign-in is blocked."}
        </ActionNote>
        <FilterButton
          variant="secondary"
          disabled={isAccountActive}
          title={
            isAccountActive
              ? "Staff is already active"
              : "Re-enable this account"
          }
          onClick={handleReactivateClick}
        >
          Re-activate staff
        </FilterButton>
        <FilterButton
          variant="primary"
          disabled={isUpdatingStaff || hasErrors}
          onClick={handleSave}
        >
          {isUpdatingStaff ? "Saving..." : "Save"}
        </FilterButton>
      </SectionActions>
    </SectionCard>
  );
}
